import React, {
  forwardRef,
  Ref,
  useId,
  useState,
  useImperativeHandle,
  useCallback,
} from 'react';

// Types
import { InputIndexProps, ImperativeRefInputIndex } from './types';

// Components Styled
import { Container, InputValue, Label } from './styles';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';
import isEqual from 'lodash.isequal';

/**
 * Primary UI component for index filtering
 */
function InputIndex(
  {
    label = 'Index',
    disabled,
    onChangeInputIndex,
    index,
    ...props
  }: InputIndexProps,
  innerRef: Ref<ImperativeRefInputIndex>
): JSX.Element {
  const [inputValue, setInputValue] = useState<string>(
    index !== undefined ? index.toString() : ''
  );

  const onChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Solo permitir números
      if (value === '' || /^\d+$/.test(value)) {
        setInputValue(value);
        const numericValue = value === '' ? undefined : parseInt(value, 10);
        onChangeInputIndex(numericValue);
      }
    },
    [onChangeInputIndex]
  );

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setInputValue('');
      onChangeInputIndex(undefined);
    },
  }));

  const id = useId();
  const isLandscapeTablet = useIsLandscapeTablet();

  return (
    <Container {...props}>
      {label && (
        <Label
          component={'label'}
          htmlFor={`${id}-index-input`}
          colorText="black"
          size={isLandscapeTablet ? 'XS' : 'S'}
        >
          {label}
        </Label>
      )}
      <InputValue
        id={`${id}-index-input`}
        type="text"
        disabled={disabled}
        maxLength={4}
        placeholder={'0000'}
        value={inputValue}
        onChange={onChangeInput}
        onKeyDown={(e) => {
          // Prevenir caracteres especiales
          /[+*./,\\-]/.test(e.key) && e.preventDefault();
        }}
      />
    </Container>
  );
}

export default React.memo(forwardRef(InputIndex), isEqual);
